import { countCatalogRegions, type DataSnapshot, type ModelOffering } from '@bedrock-profiles/shared';
import { getModelOfferings } from '../utils/modelOfferings';
import { groupIntoFamilies, type ModelFamily } from '../utils/modelFamilies';
import { groupIntoUnifiedModels, type UnifiedModel } from '../utils/unifiedModels';

export interface CatalogReadModel {
  snapshot?: DataSnapshot;
  offerings: ModelOffering[];
}

export function createCatalogReadModel(snapshot?: DataSnapshot): CatalogReadModel {
  return {
    snapshot,
    offerings: getModelOfferings(snapshot),
  };
}

export function getCatalogOfferings(snapshot?: DataSnapshot): ModelOffering[] {
  return createCatalogReadModel(snapshot).offerings;
}

export function getActiveCatalogOfferings(snapshot?: DataSnapshot): ModelOffering[] {
  return getCatalogOfferings(snapshot).filter((offering) => offering.status !== 'REMOVED');
}

export function getCatalogOfferingCount(snapshot?: DataSnapshot): number {
  const offerings = getCatalogOfferings(snapshot);
  if (offerings.length > 0) return offerings.length;
  return snapshot?.metadata.totalProfiles ?? snapshot?.profiles.length ?? 0;
}

export function getActiveCatalogOfferingCount(snapshot?: DataSnapshot): number {
  const offerings = getCatalogOfferings(snapshot);
  const activeOfferings = getActiveCatalogOfferings(snapshot);
  if (offerings.length > 0) return activeOfferings.length;
  return snapshot?.metadata.totalProfiles ?? snapshot?.profiles.length ?? 0;
}

// Grouping ~1,500 offerings is not free and many components need it for the
// same snapshot object, so results are cached per snapshot.
const unifiedModelCache = new WeakMap<DataSnapshot, UnifiedModel[]>();

export function getCatalogUnifiedModels(snapshot?: DataSnapshot): UnifiedModel[] {
  if (!snapshot) return [];
  const cached = unifiedModelCache.get(snapshot);
  if (cached) return cached;
  const models = groupIntoUnifiedModels(getCatalogOfferings(snapshot));
  unifiedModelCache.set(snapshot, models);
  return models;
}

/**
 * Distinct deployment regions across the catalogue. Run metadata
 * (`regionsSucceeded`) lists what the last collection queried, which is just
 * "global" after a partial run, so it is not a region count.
 */
export function getCatalogRegionCount(snapshot?: DataSnapshot): number {
  if (!snapshot) return 0;
  const offerings = getActiveCatalogOfferings(snapshot);
  if (offerings.length > 0) return countCatalogRegions(offerings);
  return snapshot.metadata.totalRegions ?? snapshot.regions?.length ?? 0;
}

export function getCatalogModelFamilies(snapshot?: DataSnapshot): ModelFamily[] {
  return groupIntoFamilies(getCatalogOfferings(snapshot));
}

export function searchCatalogOfferings(snapshot: DataSnapshot | undefined, query: string): ModelOffering[] {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return [];
  const terms = normalizedQuery.split(/\s+/).filter(Boolean);

  return getActiveCatalogOfferings(snapshot).filter((offering) => {
    const searchable = [
      offering.offeringId,
      offering.displayName,
      offering.model.modelId,
      offering.model.modelName,
      offering.model.provider,
      offering.deployment.cloudProvider,
      offering.deployment.regionalGroup ?? '',
      offering.description,
      offering.residency?.scope ?? '',
    ]
      .join(' ')
      .toLowerCase();

    return terms.every((term) => searchable.includes(term));
  });
}
